import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaPlus, FaEdit, FaTrash, FaSync, FaBook } from "react-icons/fa";
import { useAuth } from "../../context/AuthContext";
import PageHeader from "../../core/common/header/PageHeader";

interface CourseType {
  id: number;
  title: string;
  price: number;
  installmentCount?: number;
  paymentInfo?: string;
  priceOptions?: { level: string; amount: number }[];
  startDate?: string;
  registrationDeadline?: string;
}

const ManageCourses: React.FC = () => {
  const { logout } = useAuth();
  const nav = useNavigate();
  const [courses, setCourses] = useState<CourseType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCourses = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/admin/courses`,
        {
          credentials: "include",
        }
      );
      if (!res.ok) {
        if (res.status === 401) logout();
        throw new Error(`Failed to load (${res.status})`);
      }
      setCourses(await res.json());
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCourses();
  }, [logout]);

  const handleDelete = async (id: number) => {
    if (!window.confirm("Are you sure you want to delete this course?")) return;
    const res = await fetch(
      `${process.env.REACT_APP_API_BASE_URL}/api/admin/courses/${id}`,
      {
        method: "DELETE",
        credentials: "include",
      }
    );
    if (!res.ok) return alert(`Delete failed (${res.status})`);
    fetchCourses();
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <PageHeader
        icon={<FaBook />}
        title="Manage Courses"
        breadcrumb={[
          { label: "Home", to: "/" },
          { label: "Dashboard", to: "/admin/dashboard" },
          { label: "Manage Courses" },
        ]}
      />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* New Course Button */}
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl sm:text-3xl font-extrabold text-[#009fe3] tracking-tight">
            All Courses
          </h2>
          <div className="flex items-center gap-2">
            <button
              onClick={fetchCourses}
              disabled={loading}
              className="flex items-center gap-2 px-3 py-2 bg-[#009fe3]/10 hover:bg-[#009fe3]/20 text-[#009fe3] rounded-lg transition-colors disabled:opacity-50"
            >
              <FaSync className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
              <span className="hidden sm:inline">Refresh</span>
            </button>
            <button
              onClick={() => nav("/admin/create-course")}
              className="inline-flex items-center bg-[#009fe3] text-white px-4 py-2 rounded-lg hover:bg-[#007cb5] transition-colors shadow font-semibold"
            >
              <FaPlus className="mr-2" /> New Course
            </button>
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="bg-white rounded-xl shadow-lg p-12 text-center">
            <FaSync className="animate-spin h-12 w-12 text-[#009fe3] mx-auto mb-4" />
            <p className="text-gray-600">Loading courses...</p>
          </div>
        ) : error ? (
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 text-red-700 rounded">
              <p className="font-semibold">Error</p>
              <p>{error}</p>
            </div>
            <button
              onClick={fetchCourses}
              className="w-full bg-[#009fe3] hover:bg-[#007cb5] text-white py-3 rounded-lg font-semibold transition-colors"
            >
              Retry
            </button>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead className="bg-[#009fe3] text-white">
                  <tr>
                    {[
                      "ID",
                      "Title",
                      "One-time Price",
                      "Inst. Count",
                      "Payment Info",
                      "Price Options",
                      "Start Date",
                      "Deadline",
                      "Actions",
                    ].map((h) => (
                      <th
                        key={h}
                        className="px-6 py-3 text-sm font-medium uppercase text-left"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {courses.map((c) => (
                    <tr key={c.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm">{c.id}</td>
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">
                        {c.title}
                      </td>
                      <td className="px-6 py-4 text-sm">{c.price} BDT</td>
                      <td className="px-6 py-4 text-sm">
                        {c.installmentCount || "-"}
                      </td>
                      <td className="px-6 py-4 text-sm">
                        {c.paymentInfo || "-"}
                      </td>
                      <td className="px-6 py-4 text-sm">
                        {c.priceOptions?.length
                          ? c.priceOptions
                              .map((o) => `${o.level}: ${o.amount} BDT`)
                              .join(", ")
                          : "-"}
                      </td>
                      <td className="px-6 py-4 text-sm">
                        {c.startDate || "-"}
                      </td>
                      <td className="px-6 py-4 text-sm">
                        {c.registrationDeadline || "-"}
                      </td>
                      <td className="px-6 py-4 text-sm">
                        <div className="flex gap-2">
                          <button
                            onClick={() => nav(`/admin/edit-course/${c.id}`)}
                            className="inline-flex items-center px-3 py-1 bg-[#009fe3] text-white rounded hover:bg-[#007cb5] transition-colors"
                          >
                            <FaEdit className="mr-1" /> Edit
                          </button>
                          <button
                            onClick={() => handleDelete(c.id)}
                            className="inline-flex items-center px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
                          >
                            <FaTrash className="mr-1" /> Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {courses.length === 0 && (
                    <tr>
                      <td
                        colSpan={9}
                        className="px-6 py-10 text-center text-gray-500"
                      >
                        No courses found. Click "New Course" to create one.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default ManageCourses;
